import React, { useEffect } from 'react';

// import { DashboardOutlined } from '@ant-design/Icons';
import { accessControlUtils, hasAccessRow } from '@/components/AccessControlUtils';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import Icons from '@/components/third-party/Icons';
import * as Lucide from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { waitingHours } from '@/components/SupervisorSignOffControl';
import { useSignoffQueue } from '@/components/signoff/signoffQueueStore';
import useInspectionPresence from '@/pages/inspection/useInspectionPresence';
import { useLifecycleInboxCounts } from '@/components/lifecycle/inboxCountsStore';

// ==============================|| MENU ITEMS ||============================== //

function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const iconSize = 20;
  const selectedModule = useSelector((state) => state.moduleView);
  const { t } = useTranslation();

  //selectedModule === "prod" ? navigate() : navigate();
  useEffect(() => {
    if (location.pathname === '/recipe-view' || location.pathname === '/pack-mbpr') {
      // 2026-10-29 · only when the module's page differs: navigating to the same page dropped its query
      // (/recipe-view?archived=1 lost the archived view on every load).
      const target = selectedModule === 'prod' ? '/recipe-view' : '/pack-mbpr';
      if (location.pathname !== target) navigate(target);
    }
  }, [selectedModule, location.pathname, navigate]);


  // Every group is one level deep (group → item). W1-9: an Execution item is gated on the module its page's
  // endpoints resolve to on the server (docs/screen-register.json; test_exr_w1_module_gate compares the two).
  const ac = (key) => accessControlUtils(key)?.read_access;
  const anyOf = (...keys) => keys.some((k) => ac(k));

  // F13 / EXV-037 — the live pending-count chip on "My Sign-off Queue". One shared store with the queue page,
  // re-read after every signature (invalidateSignoffQueue), so the count never goes stale. The server says who
  // may counter-sign (`may_countersign`) and counts only the rows this person may sign. Only users who can read
  // the queue's endpoint ask for it.
  const signoff = useSignoffQueue({ enabled: Boolean(ac('My Sign-off Queue')) });
  const inspection = useInspectionPresence({ enabled: Boolean(ac('Inspection & Reconciliation')) });
  const oldestSignoff = signoff.rows.reduce((h, r) => Math.max(h, waitingHours(r) || 0), 0);
  const signoffChip =
    signoff.pendingCount > 0 ? { label: String(signoff.pendingCount), color: oldestSignoff > 24 ? 'red' : 'blue' } : null;
  // LIFECYCLE-01 step 6 · My actions: shown to every logged-in person (the inbox endpoint filters by role and
  // readable type); the badge is the server's own count (counts_only=1), red when a record could not be read.
  // The "My Actions" module switches it off per role; a server that has not set the module up yet (no row in this
  // login's access list) keeps showing it, as before.
  const showMyActions = !hasAccessRow('My Actions') || Boolean(ac('My Actions'));
  const lifecycleInbox = useLifecycleInboxCounts({ enabled: showMyActions });
  const myActionsTotal = Number(lifecycleInbox.counts?.total) || 0;
  const myActionsChip = myActionsTotal > 0
    ? { label: String(myActionsTotal), color: Number(lifecycleInbox.counts?.unreadable) > 0 ? 'red' : 'blue' }
    : null;
  const item = (id, title, url, IconCmp, showMenu, extra = {}) => ({
    id,
    title,
    type: 'item',
    icon: () => <IconCmp size={iconSize} />,
    url,
    breadcrumbs: true,
    showMenu,
    ...extra,
  });
  const group = (id, title, IconCmp, children, showMenu) => ({
    id,
    title,
    type: 'collapse',
    icon: () => <IconCmp size={iconSize} />,
    breadcrumbs: true,
    showMenu: showMenu === undefined ? children.some((c) => c.showMenu) : showMenu,
    children,
  });

  // 2026-10-29 (owner) · menu streamlining (eBMR Menu Streamlining Proposal, 27 Oct): six groups, daily work first
  // and set-up last. Look-alike entries became one, and screens that belong to one job became tabs of one page
  // (pages/hubs/MenuHubs.jsx, MenuHub). Every old route still opens its page; `aliases` lists the routes an entry
  // now holds so the sidebar and the breadcrumbs still find it. An entry is shown to anyone who may read any of the
  // pages it holds; inside, each tab keeps its own permission.
  return [
    {
      id: 'group-dashboard',
      type: 'group',
      children: [
        item('my-actions', t('My actions'), '/my-actions', Lucide.Inbox, showMyActions, myActionsChip ? { chip: myActionsChip } : {}),

        // 1 · Production — what is running on the floor now.
        group('production', t('Production'), Icons.PlaySquare, [
          // W1-9: every list on /batches (ongoing, completed, rejected, the summary strip, the MBMR filter) is a
          // "BMR Details" read on the server — for a packaging record too — so that is the item's key. Issue Approval is
          // its tab (/batches?tab=issue-approval), no longer an entry of its own.
          item('batch_details', t('Batches'), '/batches', Icons.ClipboardList, ac('Batches')),
          // The supervisor's counter-signature queue: gated on its endpoint's module ("My Sign-off Queue") AND the server's
          // word that this person may counter-sign at all (`may_countersign`) — the client decides nothing.
          item('operation-approval', t('My Sign-off Queue'), '/operation-approval', Icons.UserCheck, ac('My Sign-off Queue') && signoff.mayCountersign, signoffChip ? { chip: signoffChip } : {}),
          // Shown only when the server says the plant has inspection (`has_inspection`).
          item('inspection-lot-board', t('Inspection & Reconciliation'), '/inspection/board', Lucide.ScanSearch, ac('Inspection & Reconciliation') && inspection.hasInspection),
          item('live-batches', t('Live Batch Monitor'), '/live-batches', Lucide.MonitorDot, ac('Live Batch Monitor')),
          item('shift-board', t('Shift Board'), '/shift-board', Lucide.SquareKanban, ac('Shift Board')),
          item('equipment-and-rooms', t('Equipment & Rooms'), '/equipment-and-rooms', Lucide.DoorOpen, ac('Equipment & Rooms'),
               { aliases: ['/equipment-rooms', '/cleaning-logs', '/equipment-logs'] }),
          // Kept until each batch links to its logbook (the proposal's "Logbook link on each batch" is not built yet).
          item('batch_logbook', t('Batch Logbook'), '/batch-logbook', Icons.BookOpen, ac('Batch Logbook')),
        ]),

        // 2 · Quality — review, release and the evidence behind them.
        group('quality', t('Quality'), Icons.Shield, [
          // Review by exception → release or reject → certified copy, as three tabs of one page.
          // DD-W1-63: Batch Release opened from Execution (BMR Details → View workflow, `from=execution`) reads as the
          // batch's lifecycle under Production (Breadcrumbs `origins`).
          item('review-release', t('Review & Release'), '/review-release', Icons.ClipboardCheck,
               ac('Review & Release'), {
                 aliases: ['/qa-review-dashboard', '/batch-workflow', '/record-exports'],
                 origins: { execution: { group: 'production', parent: 'batch_details', title: t('Batch lifecycle') } },
               }),
          item('sample_testing', t('Sample Testing'), '/sample-testing', Icons.FlaskConical, ac('Sample Testing')),
          item('ticket', t('Deviations & Tickets'), '/ticket', Icons.Ticket, ac('Deviations & Tickets')),
          item('traceability', t('Batch Traceability'), '/traceability', Icons.GitBranch,
               ac('Batch Traceability'),
               { aliases: ['/batch-genealogy-full', '/batch-traceability'] }),
          // Continued Process Verification: gated on Quality Management, the module every /bp/cpv/… endpoint is mapped to.
          item('quality-cpv', t('Continued Process Verification'), '/quality-cpv', Lucide.Activity, ac('Continued Process Verification'),
               { aliases: ['/cpv', '/cpv/plans', '/cpv/signals', '/cpv/risk-bands'] }),
          item('audit-trail', t('Audit Trail'), '/audit-trail', Lucide.FileSearch, ac('Audit Trail'),
               { aliases: ['/audit-trail-review', '/audit-logs', '/change-history-audit'] }),
        ]),

        // 3 · Planning — demand, issue and schedule.
        group('planner', t('Planning'), Icons.CalendarDays, [
          // Planner (/create-demand) and Master & Lots were two doors into one page (MasterLotPage): one entry.
          item('master_lot', t('Production Plan'), '/master-lot', Icons.Layers, selectedModule === 'prod' && ac('Production Plan'),
               { aliases: ['/create-demand'] }),
          // "Issue Batch" = create a batch from an approved MBMR/MBPR; "release" is the QA disposition (Quality).
          item('issue-batch', t('Issue Batch'), '/batches?issue=1', Lucide.PackageCheck, ac('Issue Batch')),
          item('batch_gantt', t('Batch Gantt'), '/batch-gantt', Icons.BarChart3, ac('Batch Gantt')),
          item('dispensing_scheduling', t('Dispensing Schedule'), '/dispensing-scheduling', Icons.FlaskConical, ac('Dispensing Schedule')),
        ]),

        // 4 · Insights — dashboards, charts and reports.
        group('insights', t('Insights'), Icons.LayoutDashboard, [
          // CT-07: Control Tower replaces Plant Cockpit (same access rule); the server picks the tab by role.
          item('DashBoard', t('Control Tower'), '/control-tower', Icons.LayoutDashboard, ac('Control Tower')),
          item('control-charts', t('Control Charts'), '/control-charts', Lucide.ChartLine, ac('Control Charts')),
          item('reports', t('Reports'), '/reports', Icons.FileText, anyOf('Reports', 'Report Designer Approval', 'Report Designer Export'),
               { aliases: ['/report-builder', '/report-template-config', '/report-designer'] }),
          // Graph Studio and Data Tables: gated on Quality Management, the module every /bp/apqr/… endpoint is mapped to.
          item('trend-analysis', t('Trend Analysis'), '/trend-analysis', Lucide.Gauge, ac('Trend Analysis'),
               { aliases: ['/graph-studio', '/data-tables'] }),
        ]),

        // 5 · Master Records — what a batch is made from, and its approval.
        group('Recipe', t('Master Records'), Icons.Package, [
          item('products', t('Products'), '/products', Icons.Layers, ac('Products'),
               { aliases: ['/prod-config', '/inspection-specification'] }),
          // MFR -> MBMR: the per-unit formula the MBMR is derived from. Reuses the View MBMR permission.
          item('mfr', t('Formulas (MFR)'), '/mfr', Icons.FlaskConical, ac('Formulas (MFR)')),
          // MBR and MPR as tabs (Support MR is not in use: no tab, its route stays); Scaled Copy is a button on the page.
          item('master-records', t('Master Records'), '/master-records', Icons.LayoutGrid, ac('Master Records (MBR / MPR)'),
               { aliases: ['/recipe-view', '/pack-mbpr', '/support-mbmr', '/scaled-copy'] }),
          item('process_library', t('Process Library'), '/process-library', Icons.BookOpen, ac('Process Library'), { caption: t('reusable full process') }),
          // Recipe Building Blocks — the definition masters a record is assembled from, as tabs (now also the SOP
          // documents, required attachments and clearance profiles a step links to).
          item('recipe-blocks', t('Building Blocks'), '/recipe-blocks', Lucide.Blocks,
               ac('Building Blocks'),
               { caption: t('types, step content, parameters & units, materials'),
                 aliases: ['/sop-documents', '/required-attachments', '/clearance-profiles', '/formula-library'] }),
          // Recipe, MFR, process and library approval and recipe rollout, as tabs.
          item('approvals', t('Approvals'), '/approvals', Icons.CheckCircle,
               ac('Approvals'),
               { aliases: ['/process-library-approvals'] }),
        ]),

        // 6 · Settings — every set-up page, each hub a section list beside the chosen page.
        group('settings', t('Settings'), Icons.Settings, [
          item('settings-plant-equipment', t('Plant & Equipment'), '/settings/plant-equipment', Icons.Factory, ac('Plant & Equipment'),
               { aliases: ['/roomConfig', '/areaConfig', '/machine-config', '/machine-type-config', '/sensor', '/equipment-config',
                           '/transfer-path-config', '/process-control-parameter-config', '/control-category-config', '/hierarchy-definition', '/hierarchy-levels', '/cleaningTypes'] }),
          item('settings-quality-setup', t('Quality Setup'), '/settings/quality-setup', Lucide.ListChecks, ac('Quality Setup'),
               { aliases: ['/sample-config', '/test-type', '/testing-qc', '/defect-code-library', '/inspection-stations', '/batch-scoring'] }),
          item('settings-people-access', t('People & Access'), '/settings/people-access', Icons.Users, ac('People & Access'),
               { aliases: ['/user-management', '/accessControl', '/password-policy', '/active-sessions', '/session-history',
                           '/directory-access', '/training-configuration', '/shifts'] }),
          item('settings-approval-workflows', t('Approval Workflows'), '/settings/approval-workflows', Icons.Workflow, ac('Approval Workflows'),
               { aliases: ['/approval-processes', '/workflow-design', '/bmr-workflows', '/work-flow'] }),
          item('settings-system', t('System'), '/settings/system', Icons.Settings, ac('System'),
               { aliases: ['/plant-configuration', '/company-settings', '/app-setting', '/numbering', '/licence-config', '/master-config', '/dashboard-thresholds'] }),
          item('settings-integrations', t('Integrations'), '/settings/integrations', Lucide.Plug, ac('Integrations'),
               { aliases: ['/integrations/status', '/system-integration', '/integrations/connect', '/integrations/sap',
                           '/integrations/lims', '/integrations/qms', '/event-logs'] }),
        ]),

        group('help', t('Help'), Icons.HelpCircle, [
          item('Support', t('User manual'), 'https://nexgensis-1.gitbook.io/pidilite-batch-processing-web-application/', Icons.HelpCircle, ac('User Manual'), {
            external: true,
            target: true,
            iconCollapsed: Icons.HelpCircle,
          }),
        ]),
      ].filter(Boolean),
    },
  ];
}
export default Dashboard;
